import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Ticket, Plus, Edit, Trash2, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface PromoCode {
  code: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  duration: 'once' | 'repeating' | 'forever';
  durationMonths?: number; // For repeating
  maxUses?: number;
  currentUses: number;
  expiresAt?: string;
  active: boolean;
  applicableTiers: string[]; // ['solo', 'small', 'enterprise', 'all']
  createdBy: string;
  createdAt: string;
  notes?: string;
}

interface PromoCodeManagerProps {
  userEmail: string;
  accessToken: string;
}

export function PromoCodeManager({ userEmail, accessToken }: PromoCodeManagerProps) {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);
  
  // Form state
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'percentage' | 'fixed'>('percentage');
  const [discountValue, setDiscountValue] = useState('');
  const [duration, setDuration] = useState<'once' | 'repeating' | 'forever'>('once');
  const [durationMonths, setDurationMonths] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [active, setActive] = useState(true);
  const [applicableTiers, setApplicableTiers] = useState<string[]>(['all']);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadPromoCodes();
  }, []);

  const loadPromoCodes = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/promo-codes`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load promo codes');
      }

      const data = await response.json();
      setPromoCodes(data.promoCodes || []);
    } catch (error) {
      console.error('Error loading promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingCode(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (promoCode: PromoCode) => {
    setEditingCode(promoCode);
    setCode(promoCode.code);
    setDiscountType(promoCode.discountType);
    setDiscountValue(promoCode.discountValue.toString());
    setDuration(promoCode.duration);
    setDurationMonths(promoCode.durationMonths?.toString() || '');
    setMaxUses(promoCode.maxUses?.toString() || '');
    setExpiresAt(promoCode.expiresAt || '');
    setActive(promoCode.active);
    setApplicableTiers(promoCode.applicableTiers);
    setNotes(promoCode.notes || '');
    setDialogOpen(true);
  };

  const resetForm = () => {
    setCode('');
    setDiscountType('percentage');
    setDiscountValue('');
    setDuration('once');
    setDurationMonths('');
    setMaxUses('');
    setExpiresAt('');
    setActive(true);
    setApplicableTiers(['all']);
    setNotes('');
  };

  const handleSubmit = async () => {
    try {
      // Validation
      if (!code.trim()) {
        toast.error('Promo code is required');
        return;
      }

      if (!discountValue || parseFloat(discountValue) <= 0) {
        toast.error('Valid discount value is required');
        return;
      }

      if (discountType === 'percentage' && parseFloat(discountValue) > 100) {
        toast.error('Percentage discount cannot exceed 100%');
        return;
      }

      if (duration === 'repeating' && (!durationMonths || parseInt(durationMonths) <= 0)) {
        toast.error('Duration months is required for repeating discounts');
        return;
      }

      const promoCodeData: PromoCode = {
        code: code.toUpperCase().trim(),
        discountType,
        discountValue: parseFloat(discountValue),
        duration,
        durationMonths: duration === 'repeating' ? parseInt(durationMonths) : undefined,
        maxUses: maxUses ? parseInt(maxUses) : undefined,
        currentUses: editingCode?.currentUses || 0,
        expiresAt: expiresAt || undefined,
        active,
        applicableTiers,
        createdBy: editingCode?.createdBy || userEmail,
        createdAt: editingCode?.createdAt || new Date().toISOString(),
        notes: notes.trim() || undefined,
      };

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/promo-codes${editingCode ? `/${editingCode.code}` : ''}`,
        {
          method: editingCode ? 'PUT' : 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(promoCodeData),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save promo code');
      }

      toast.success(editingCode ? 'Promo code updated' : 'Promo code created');
      setDialogOpen(false);
      resetForm();
      loadPromoCodes();
    } catch (error) {
      console.error('Error saving promo code:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save promo code');
    }
  };

  const handleDelete = async (codeToDelete: string) => {
    if (!confirm('Are you sure you want to delete this promo code?')) {
      return;
    }

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/promo-codes/${codeToDelete}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete promo code');
      }

      toast.success('Promo code deleted');
      loadPromoCodes();
    } catch (error) {
      console.error('Error deleting promo code:', error);
      toast.error('Failed to delete promo code');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Code copied to clipboard');
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCode(result);
  };

  const formatDiscount = (promoCode: PromoCode) => {
    if (promoCode.discountType === 'percentage') {
      return `${promoCode.discountValue}% off`;
    } else {
      return `$${promoCode.discountValue} off`;
    }
  };

  const formatDuration = (promoCode: PromoCode) => {
    if (promoCode.duration === 'once') {
      return 'First payment only';
    } else if (promoCode.duration === 'repeating') {
      return `${promoCode.durationMonths} months`;
    } else {
      return 'Forever';
    }
  };

  const isExpired = (promoCode: PromoCode) => {
    if (!promoCode.expiresAt) return false;
    return new Date(promoCode.expiresAt) < new Date();
  };

  const isMaxedOut = (promoCode: PromoCode) => {
    if (!promoCode.maxUses) return false;
    return promoCode.currentUses >= promoCode.maxUses;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Promo Codes</CardTitle>
          <CardDescription>Loading promo codes...</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Ticket className="h-5 w-5" />
                Promo Codes
              </CardTitle>
              <CardDescription>
                Create and manage discount codes for special offers and partnerships
              </CardDescription>
            </div>
            <Button onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Create Promo Code
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {promoCodes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No promo codes created yet. Click "Create Promo Code" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Discount</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Tiers</TableHead>
                  <TableHead>Uses</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {promoCodes.map((promoCode) => (
                  <TableRow key={promoCode.code}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="px-2 py-1 bg-gray-100 rounded text-sm font-mono">
                          {promoCode.code}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(promoCode.code)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      {promoCode.notes && (
                        <div className="text-xs text-gray-500 mt-1">{promoCode.notes}</div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatDiscount(promoCode)}
                    </TableCell>
                    <TableCell>{formatDuration(promoCode)}</TableCell>
                    <TableCell>
                      {promoCode.applicableTiers.includes('all') ? (
                        <Badge variant="outline">All Tiers</Badge>
                      ) : (
                        <div className="flex gap-1 flex-wrap">
                          {promoCode.applicableTiers.map((tier) => (
                            <Badge key={tier} variant="outline" className="text-xs">
                              {tier}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {promoCode.currentUses}
                      {promoCode.maxUses && ` / ${promoCode.maxUses}`}
                    </TableCell>
                    <TableCell>
                      {isExpired(promoCode) ? (
                        <Badge variant="destructive">Expired</Badge>
                      ) : isMaxedOut(promoCode) ? (
                        <Badge variant="secondary">Maxed Out</Badge>
                      ) : promoCode.active ? (
                        <Badge variant="default" className="bg-green-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {promoCode.expiresAt
                        ? new Date(promoCode.expiresAt).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(promoCode)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(promoCode.code)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCode ? 'Edit Promo Code' : 'Create Promo Code'}
            </DialogTitle>
            <DialogDescription>
              {editingCode
                ? 'Update the promo code details'
                : 'Create a new discount code for customers'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Code */}
            <div className="space-y-2">
              <Label htmlFor="code">Promo Code *</Label>
              <div className="flex gap-2">
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="EARLYBIRD2025"
                  className="font-mono"
                  disabled={!!editingCode}
                />
                {!editingCode && (
                  <Button type="button" variant="outline" onClick={generateRandomCode}>
                    Generate
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500">
                Use uppercase letters and numbers. Will be automatically converted to uppercase.
              </p>
            </div>

            {/* Discount Type & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discountType">Discount Type *</Label>
                <Select value={discountType} onValueChange={(v: any) => setDiscountType(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage Off</SelectItem>
                    <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="discountValue">Discount Value *</Label>
                <div className="relative">
                  {discountType === 'fixed' && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                      $
                    </span>
                  )}
                  <Input
                    id="discountValue"
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder={discountType === 'percentage' ? '50' : '25'}
                    className={discountType === 'fixed' ? 'pl-7' : ''}
                    min="0"
                    max={discountType === 'percentage' ? '100' : undefined}
                  />
                  {discountType === 'percentage' && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      %
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label htmlFor="duration">Duration *</Label>
              <Select value={duration} onValueChange={(v: any) => setDuration(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once (First payment only)</SelectItem>
                  <SelectItem value="repeating">Repeating (X months)</SelectItem>
                  <SelectItem value="forever">Forever</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Duration Months (if repeating) */}
            {duration === 'repeating' && (
              <div className="space-y-2">
                <Label htmlFor="durationMonths">Number of Months *</Label>
                <Input
                  id="durationMonths"
                  type="number"
                  value={durationMonths}
                  onChange={(e) => setDurationMonths(e.target.value)}
                  placeholder="3"
                  min="1"
                />
              </div>
            )}

            {/* Applicable Tiers */}
            <div className="space-y-2">
              <Label>Applicable Tiers *</Label>
              <div className="flex flex-wrap gap-2">
                {['all', 'solo', 'small', 'enterprise'].map((tier) => (
                  <label
                    key={tier}
                    className="flex items-center gap-2 px-3 py-2 border rounded cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={applicableTiers.includes(tier)}
                      onChange={(e) => {
                        if (tier === 'all') {
                          setApplicableTiers(e.target.checked ? ['all'] : []);
                        } else {
                          const newTiers = e.target.checked
                            ? [...applicableTiers.filter((t) => t !== 'all'), tier]
                            : applicableTiers.filter((t) => t !== tier);
                          setApplicableTiers(newTiers);
                        }
                      }}
                      className="rounded"
                    />
                    <span className="capitalize">{tier === 'small' ? 'Small Firm' : tier}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Max Uses */}
            <div className="space-y-2">
              <Label htmlFor="maxUses">Maximum Uses (Optional)</Label>
              <Input
                id="maxUses"
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="Leave empty for unlimited"
                min="1"
              />
              <p className="text-xs text-gray-500">
                Limit how many times this code can be used. Leave empty for unlimited uses.
              </p>
            </div>

            {/* Expiration Date */}
            <div className="space-y-2">
              <Label htmlFor="expiresAt">Expiration Date (Optional)</Label>
              <Input
                id="expiresAt"
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
              <p className="text-xs text-gray-500">
                Code will become invalid after this date. Leave empty for no expiration.
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., CEO Partnership Deal - Company XYZ"
              />
              <p className="text-xs text-gray-500">
                Internal notes to remember what this code is for.
              </p>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 border rounded">
              <div>
                <Label>Active Status</Label>
                <p className="text-xs text-gray-500">
                  Inactive codes cannot be used, but existing subscriptions continue
                </p>
              </div>
              <Switch checked={active} onCheckedChange={setActive} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>
              {editingCode ? 'Update' : 'Create'} Promo Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
